import { getAuth, signInWithCustomToken, updateEmail } from "firebase/auth";
import NotificationsAPI from "../API/notifications.api";
import {
    getFirestore,
    collection,
    query,
    orderBy,
    limit,
    where,
    onSnapshot,
    setDoc,
    updateDoc,
    doc,
    arrayUnion,
    startAfter,
    getDocs,
} from "firebase/firestore";
import { getToken, onMessage } from "firebase/messaging";
import { onBackgroundMessage } from "firebase/messaging/sw";
import { messaging, firebaseApp } from "../config/firebase.config";
import LogCapture from "../utils/LogManagement/LogHelper";
import { Subject } from "rxjs";

const subject = new Subject();
const progressUpdatesSubject = new Subject();

const notificationService = {
    sendMessage: (messages) => subject.next(messages),
    clearMessages: () => subject.next(),
    getMessage: () => subject.asObservable(),
    unsubscribe: () => subject.unsubscribe(),
};

const progressUpdatesService = {
    sendProgressUpdates: (result) => progressUpdatesSubject.next(result),
    getServiceObservable: () => progressUpdatesSubject.asObservable(),
    unsubscribe: () => progressUpdatesSubject.unsubscribe(),
};

const firebaseSignInWithCustomToken = async (email) => {
    LogCapture.info("Firebase helper: firebaseSignInWithCustomToken");
    const firebaseToken = await NotificationsAPI.customToken();
    LogCapture.info(
        `User ${email} signed in to get custom token for notification cente`
    );

    const auth = getAuth(firebaseApp);
    await signInWithCustomToken(auth, firebaseToken);

    // Now that the user is authenticated, you can use auth-enabled
    // Firebase services. In this example, we update the Firebase
    // user profile with information from Okta:
    const user = auth.currentUser;
    if (user?.email !== email) {
        await updateEmail(user, email);
    }
};

const readNotifications = async () => {
    LogCapture.info("Firebase helper: readNotifications");
    const notificationIdsQuery = query(
        collection(getFirestore(), "users_notifications"),
        where("userId", "==", getAuth().currentUser?.uid),
        where("read", "==", false)
    );
    const notificationIdsQuerySnapshot = await getDocs(notificationIdsQuery);
    const notificationIds =
        notificationIdsQuerySnapshot.docs.map((doc) => doc.id) || [];

    notificationIds.forEach(async (notificationId) => {
        const tokenRef = doc(
            getFirestore(),
            `/users_notifications/${notificationId}`
        );
        await updateDoc(tokenRef, {
            read: true,
        });
    });

    LogCapture.info("User successfully read new notification");
};

const getNotifications = async (lastVisible = {}) => {
    LogCapture.info("Firebase helper: getNotifications");
    try {
        if (lastVisible === -1) {
            return { lastVisible: -1, records: [] };
        }

        const lastUpdate = await NotificationsAPI.getLastSeen();
        if (!lastUpdate && !lastVisible?.timestamp) return;

        const notificationIdsQuery = query(
            collection(getFirestore(), "users_notifications"),
            where("userId", "==", getAuth().currentUser?.uid),
            orderBy("timestamp", "desc"),
            startAfter(
                lastVisible.timestamp
                    ? lastVisible.timestamp
                    : new Date(lastUpdate)
            ),
            limit(5)
        );
        const notificationIdsQuerySnapshot = await getDocs(
            notificationIdsQuery
        );
        const notificationIds =
            notificationIdsQuerySnapshot.docs.map(
                (doc) => doc.data().notificationId
            ) || [];

        if (notificationIds.length === 0)
            return { lastVisible: -1, records: [] };

        const notificationsQuery = query(
            collection(getFirestore(), "notifications"),
            where("notificationId", "in", notificationIds),
            orderBy("timestamp", "desc")
        );

        const documentSnapshots = await getDocs(notificationsQuery);
        const records = documentSnapshots.docs.map((doc) => doc.data()) || [];

        const lastRecord =
            records.length < 5 ? -1 : records[records.length - 1];

        return { lastVisible: lastRecord, records };
    } catch (error) {
        LogCapture.info(
            `Failed to retrieve notifications error for getNotifications: ${error}`
        );
    }
};

const onNotificationsSnapshotListener = async () => {
    LogCapture.info("Firebase helper: onNotificationsSnapshotListener");
    const notificationIdsQuery = async () => {
        try {
            const lastUpdate = await NotificationsAPI.getLastSeen();
            if (!lastUpdate) return;

            return query(
                collection(getFirestore(), "users_notifications"),
                where("userId", "==", getAuth().currentUser?.uid),
                where("timestamp", ">", new Date(lastUpdate)),
                orderBy("timestamp", "desc")
            );
        } catch (error) {
            LogCapture.info(
                `Failed to retrieve notifications error for onNotificationsSnapshotListener: ${error}`
            );
        }
    };

    onSnapshot(await notificationIdsQuery(), async (snapshot) => {
        LogCapture.info("Firebase helper: onSnapshot");
        const notificationIds = snapshot.docs.map(
            (doc) => doc.data().notificationId
        );

        if (notificationIds.length !== 0) {
            const messagesQuery = query(
                collection(getFirestore(), "notifications"),
                where("notificationId", "in", notificationIds),
                orderBy("timestamp", "desc")
            );

            const notifications = await getDocs(messagesQuery);

            const messages = notifications.docs.map((doc) => doc.data());

            notificationService.sendMessage(messages);
        } else notificationService.sendMessage([]);
    });
};

const onTaskSnapshotListener = async (taskId, type) => {
    LogCapture.info("Firebase helper: onTaskSnapshotListener");
    const taskQuery = query(
        collection(getFirestore(), "backend_actions"),
        where("userId", "==", getAuth().currentUser?.uid),
        where("taskId", "==", taskId),
        where("type", "==", type)
    );

    return onSnapshot(taskQuery, async (snapshot) => {
        LogCapture.info(
            "Firebase helper: onTaskSnapshotListener => onSnapshot"
        );
        if (snapshot.docs.length !== 0) {
            const result = snapshot.docs.map((doc) => doc.data());
            progressUpdatesService.sendProgressUpdates(result);
        } else progressUpdatesService.sendProgressUpdates([]);
    });
};

const onBackgroundMessageListener = () => {
    LogCapture.info("Firebase helper: onBackgroundMessageListener");
    return new Promise((resolve) => {
        onBackgroundMessage(messaging, (payload) => {
            resolve(payload);
        });
    });
};

const onMessageListener = () => {
    LogCapture.info("Firebase helper: onMessageListener");
    return new Promise((resolve) => {
        onMessage(messaging, (payload) => {
            resolve(payload);
        });
    });
};

const saveMessagingDeviceToken = async (currentToken) => {
    LogCapture.info("Firebase helper: saveMessagingDeviceToken");
    const tokenRef = doc(getFirestore(), "users", getAuth().currentUser.uid);

    const userQuery = query(
        collection(getFirestore(), "users"),
        where("uid", "==", getAuth().currentUser?.uid)
    );
    const userQuerySnapshot = await getDocs(userQuery);
    const users = userQuerySnapshot.docs.map((doc) => doc.data()) || [];

    if (users[0]?.uid !== getAuth().currentUser.uid)
        await setDoc(tokenRef, { uid: getAuth().currentUser.uid });
    await updateDoc(tokenRef, {
        uid: getAuth().currentUser.uid,
        notificationTokens: arrayUnion(currentToken),
    });
};

const requestPermission = async () => {
    LogCapture.info("Firebase helper: requestPermission");
    const permission = await Notification.requestPermission();

    if (permission === "granted") {
        LogCapture.info("push notifications permission granted");

        await getFirebaseToken();
    } else {
        LogCapture.info("push notifications permission denyed");
    }
};

const getFirebaseToken = async (setTokenFound = null) => {
    LogCapture.info("Firebase helper: getFirebaseToken");
    return getToken(messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
    })
        .then((currentToken) => {
            if (currentToken) {
                setTokenFound && setTokenFound(true);
                saveMessagingDeviceToken(currentToken);
                LogCapture.info("registration token retrieved");

                return currentToken;

                // Track the token -> client mapping, by sending to backend server
                // show on the UI that permission is secured
            } else {
                LogCapture.error(
                    "No registration token available. Request permission to generate one."
                );

                // requestPermission();
                setTokenFound && setTokenFound(false);
                // shows on the UI that permission is required
                return null;
            }
        })
        .catch((error) => {
            LogCapture.info(
                `Failed to retrieve notification token error: ${error}`
            );
        });
};

export {
    notificationService,
    progressUpdatesService,
    getFirebaseToken,
    firebaseSignInWithCustomToken,
    readNotifications,
    onMessageListener,
    requestPermission,
    onBackgroundMessageListener,
    onNotificationsSnapshotListener,
    getNotifications,
    onTaskSnapshotListener,
};
